import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getActivePostShortNameBase } from '../../services/postSelection';
import { isPostRestrictedFromAssignedQueries } from '../../services/restrictedPostsConfig';

/**
 * Custom hook to get user permissions based on role
 * Extracts role normalization and permission checks for reusability
 * 
 * @returns {Object} Permissions object with boolean flags
 */
export const useUserPermissions = () => {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    const roleValue = user?.role || user?.roleName || user?.userRole || '';
    const roleNormalized = (roleValue || '').toString().trim().toLowerCase();

    const isAdmin = roleNormalized === 'admin' || roleNormalized === 'superadmin';
    const isChairRole = roleNormalized === 'ch' || roleNormalized === 'chairperson' || roleNormalized === 'chairman';
    const canViewAnalyticsDashboard = isAdmin || isChairRole || roleNormalized === 'mp' || roleNormalized === 'ed';
    const canViewMyQueries = /* roleNormalized !== 'no' && */ roleNormalized !== 'gm' && !isAdmin; // REMOVED: NO role deprecated

    // Blocked only while the active post is configured as restricted (see
    // RESTRICTED_ASSIGNED_QUERIES_POSTS server-side), not by role — every
    // other post (any role) can view it.
    const isRestrictedPost = isPostRestrictedFromAssignedQueries(
      getActivePostShortNameBase(user)
    );
    const canViewAssignedQueries = !isAdmin && !isRestrictedPost;

    // Escalated/Notified queries are for eligible non-admin roles.
    // GM should not see this section in the sidebar.
    const canViewEscalatedQueries = !isAdmin && roleNormalized !== 'gm';

    return {
      roleNormalized,
      isAdmin,
      isChairRole,
      canViewAnalyticsDashboard,
      canViewMyQueries,
      canViewAssignedQueries,
      canViewEscalatedQueries,
    };
  }, [user?.role, user?.roleName, user?.userRole, user?.canBeAssignee, user?.posts, user?.activePostId, user?.mainPostId]);

  return permissions;
};
